// frontend/src/pages/profile.jsx
import { useEffect, useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { BookOpenIcon, DocumentTextIcon } from '@heroicons/react/24/outline'; // Example icons

// Helper function to group chapters by type
const groupChaptersByType = (chapters) => {
  return chapters.reduce((acc, chapter) => {
    const type = chapter.type || 'unknown'; // Default group if type is missing
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(chapter);
    return acc;
  }, {});
};

// Define the display order and titles for chapter types
const chapterTypeOrder = ['workshop', 'workshop2', 'chapter', 'chapter1', 'chapter2', 'chapter3', 'chapter4', 'chapter5', 'chapter6', 'chapter7', 'chapter8'];
const chapterTypeTitles = {
  workshop: 'Introduction', // Will be shown to 'student'
  'workshop2': 'Introduction', // Will be shown to 'student2'
  chapter: 'Unit 1',
  chapter2: 'Unit 2',
  chapter3: 'Unit 3',
  chapter4: 'Unit 4',
  chapter5: 'Unit 5',
  chapter6: 'Unit 6',
  chapter7: 'Unit 7',
  chapter8: 'Unit 8',
  unknown: 'Other' // Fallback category
};

// Helper function to clean chapter titles for display
const formatDisplayTitle = (title) => {
  if (!title) return '';
  const prefixRegex = /^(workshop|chapter|module)\s?\d+:\s*/i;
  return title.replace(prefixRegex, '').trim();
};

// Define workshop order explicitly
const WORKSHOP_ORDER_MAP = {
  "6761d34084ae11fcf934dc66": 1, // Workshop 1
  "6761d34084ae11fcf934dc9f": 2, // Workshop 2
  "6761d34084ae11fcf934dccf": 3, // Workshop 3
};

function Profile() {
  const [user, setUser] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedType, setSelectedType] = useState(chapterTypeOrder[0]);
  const { isAuthenticated, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Combined loading state

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true); // Start loading
      const fetchData = async () => {
        try {
          // Fetch profile (includes user+feedback), chapters, and progress
          const [userRes, chaptersRes, progressRes] = await Promise.all([
            fetch(`${import.meta.env.VITE_API_URL}/api/auth/profile`, { // Fetches user with feedback
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${import.meta.env.VITE_API_URL}/api/chapters`),
            fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, { // Fetches answers and completedChapters
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

          if (userRes.ok && chaptersRes.ok && progressRes.ok) {
            const userData = await userRes.json();
            const chaptersData = await chaptersRes.json();
            const progressData = await progressRes.json();

            setUser(userData.user); // User object now includes feedback
            setChapters(chaptersData);
            setProgress(progressData);
          } else {
            console.error('Error fetching data: One or more requests failed.');
             if (userRes.status === 401 || progressRes.status === 401) {
               console.error('Unauthorized access detected.');
               navigate('/login');
             } else {
               // Handle other errors
             }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false); // Stop loading regardless of outcome
        }
      };

      fetchData();
    }
  }, [isAuthenticated, token, navigate]);

  // --- Role-Based Locking Logic ---
  const lockStatusMap = {};
  if (progress && user) {
    if (user.role === 'admin') {
      chapters.forEach(chapter => { lockStatusMap[chapter._id] = false; });
    } else {
      let roleAdjustedChapterTypeOrder = [...chapterTypeOrder];

      if (user.role === 'student') {
        // Students see V1 workshops and not V2 workshops in their progression
        roleAdjustedChapterTypeOrder = chapterTypeOrder.filter(type => type !== 'workshop2');
      } else if (user.role === 'student2') {
        // Student2s see V2 workshops and not V1 workshops or regular chapters in their progression
        roleAdjustedChapterTypeOrder = chapterTypeOrder.filter(
          type => type === 'workshop2' // Only allow 'workshop2' for student2 progression
        );
      } else if (user.role === 'pro') {
        // Pro users see V2 workshops and all chapter types, but not V1 workshops
        roleAdjustedChapterTypeOrder = chapterTypeOrder.filter(type => type !== 'workshop');
      } else {
        // Other non-admin roles (e.g., creator) might default to V1 or see all.
        // For now, let's assume they follow V1 path like regular students if not specified.
        roleAdjustedChapterTypeOrder = chapterTypeOrder.filter(type => type !== 'workshop2');
      }

      const chaptersForLockingSequence = chapters
        .filter(ch => roleAdjustedChapterTypeOrder.includes(ch.type))
        .sort((a, b) => {
          const typeAIndex = roleAdjustedChapterTypeOrder.indexOf(a.type);
          const typeBIndex = roleAdjustedChapterTypeOrder.indexOf(b.type);

          if (typeAIndex !== typeBIndex) return typeAIndex - typeBIndex;

          if (a.type === 'workshop') { // V1 workshops
            return (WORKSHOP_ORDER_MAP[a._id] || 99) - (WORKSHOP_ORDER_MAP[b._id] || 99);
          }
          if (a.type === 'workshop2') { // V2 workshops
            return a.title.localeCompare(b.title); // Sort V2 by title, or use a V2_WORKSHOP_ORDER_MAP if available
          }
          return a.title.localeCompare(b.title); // Default sort for other types
        });

      for (let i = 0; i < chaptersForLockingSequence.length; i++) {
        const currentChapter = chaptersForLockingSequence[i];
        if (i === 0) {
          lockStatusMap[currentChapter._id] = false;
        } else {
          const previousChapter = chaptersForLockingSequence[i - 1];
          const isPreviousCompleted = progress.completedChapters.some(c => c._id === previousChapter._id);
          lockStatusMap[currentChapter._id] = !isPreviousCompleted;
        }
      }
    }
  }
  // --- END: Role-Based Locking Logic ---

  const allGroupedChapters = groupChaptersByType(chapters);
  // Adjust availableTypes based on role to hide irrelevant workshop versions
  let roleFilteredChapterTypeOrder = [...chapterTypeOrder];
  if (user) { // Ensure user object is available
    if (user.role === 'student') {
      roleFilteredChapterTypeOrder = chapterTypeOrder.filter(type => type !== 'workshop2');
    } else if (user.role === 'student2') {
      // Student2 should see 'workshop2' and all 'chapter' types, but not 'workshop' (v1)
      roleFilteredChapterTypeOrder = chapterTypeOrder.filter(
        type => type !== 'workshop'
      );
    } else if (user.role === 'pro') {
      // Pro users should see 'workshop2' and all 'chapter' types, but not 'workshop' (v1)
      roleFilteredChapterTypeOrder = chapterTypeOrder.filter(
        type => type !== 'workshop'
      );
    }
    // Admins and other roles will see all types present in chapterTypeOrder
  }
  const availableTypes = roleFilteredChapterTypeOrder.filter(type => allGroupedChapters[type]?.length > 0);

  useEffect(() => {
    if (availableTypes.length > 0 && !availableTypes.includes(selectedType)) {
      setSelectedType(availableTypes[0]);
    }
  }, [availableTypes, selectedType]);

  // Use the combined loading state
  if (loading || isAuthenticated === null) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-white">
        <div className="w-80 mb-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-black rounded-full transition-all duration-1000 ease-out"
              style={{
                width: '85%',
                animation: 'loadingProgress 0.25s ease-out forwards'
              }}
            />
          </div>
        </div>
        <style jsx>{`
          @keyframes loadingProgress {
            0% { width: 0% }
            50% { width: 60% }
            80% { width: 85% }
            100% { width: 95% }
          }
        `}</style>
      </div>
    );
  }

  // Handle case where data fetching failed but didn't redirect
  if (!user || !progress) {
     return <div className="m-20 text-center text-red-500">Failed to load profile data. Please try again later.</div>;
  }

  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  if (!user || !progress) return <div>Loading...</div>;

  const workshopChapters = chapters.filter(ch => ch.title.substring(0, 8) === 'Workshop');
  const chapterChapters = chapters.filter(ch => ch.title.substring(0, 7) === 'Chapter');

  console.log('workshopChapters:', workshopChapters);
  console.log('chapterChapters:', chapterChapters);

  // For workshops, non-admin users might only see specific ones.
  const displayedWorkshops = user.role === 'admin' 
    ? workshopChapters 
    : workshopChapters.filter(ch =>
        ch._id === '6761d34084ae11fcf934dc66' ||
        ch._id === '6761d34084ae11fcf934dc9f' ||
        ch._id === '6761d34084ae11fcf934dccf'
      );
  const totalWorkshops = user.role === 'admin' ? workshopChapters.length : 3;

  // Calculate the number of completed full chapters
  const completedFullChapters = progress ? progress.completedChapters.filter(completedId => 
    chapterChapters.some(chapter => chapter._id === completedId)
  ).length : 0;
  
  const totalFullChapters = chapterChapters.length;

  return (
    <div className='min-h-screen bg-gray-50'>
      <div className="max-w-8xl mx-auto pt-8 px-4 sm:px-6 lg:px-8">

        {/* Always render the courses view */}
        <div className="flex flex-col md:flex-row md:space-x-8">
          {/* Vertical Navigation (Courses View) */}
          <aside className="md:w-1/4 lg:w-1/5 mb-8 md:mb-0">
            <nav className="space-y-1" aria-label="Sidebar">
              {availableTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`w-full flex items-center px-3 py-2 font-body text-md rounded-md transition-colors duration-150 ease-in-out text-left ${
                    selectedType === type
                      ? 'bg-gray-200 text-black'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  aria-current={selectedType === type ? 'page' : undefined}
                >
                  <span className="truncate">
                    {chapterTypeTitles[type] || `Module: ${type}`}
                  </span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content Area (Courses View) */}
          <main className="flex-1">
            {availableTypes.map(type => {
              if (type !== selectedType) return null;

              const chaptersOfType = allGroupedChapters[type] || [];
              let displayChapters = [...chaptersOfType];

              if (type === 'workshop') {
                // V1 workshops, sorted by WORKSHOP_ORDER_MAP
                displayChapters.sort((a, b) => (WORKSHOP_ORDER_MAP[a._id] || 99) - (WORKSHOP_ORDER_MAP[b._id] || 99));
              } else if (type === 'workshop2') {
                // V2 workshops, sorted by title (or a specific V2 order map)
                displayChapters.sort((a, b) => a.title.localeCompare(b.title));
              } else {
                 displayChapters.sort((a, b) => a.title.localeCompare(b.title));
              }

              return (
                <div key={type}>
                  <div className="grid grid-cols-1 sm:grid-cols-1 xl:grid-cols-2 gap-6">
                    {displayChapters.map((chapter, idx) => {
                      const isCompleted = progress.completedChapters.some((c) => c._id === chapter._id);
                      
                      let currentNeedsUpgrade = false;
                      // 'student' role needs upgrade for non-workshop (i.e., 'chapter') types
                      if (user.role === 'student' && chapter.type !== 'workshop') {
                        currentNeedsUpgrade = true;
                      } else if (user.role === 'student2' && chapter.type.startsWith('chapter')) {
                        // 'student2' role needs upgrade for any 'chapter' types (e.g., chapter, chapter1, chapter2)
                        currentNeedsUpgrade = true;
                      }
                      // 'pro' users don't need upgrades for any content they can see
                      // For 'admin', or other roles, currentNeedsUpgrade remains false by default,
                      // meaning no upgrade is needed based on chapter type for them.
                      
                      // Replace previous needsUpgrade with currentNeedsUpgrade
                      const needsUpgrade = currentNeedsUpgrade;

                      let isLocked = true;

                      if (user.role === 'admin') {
                        isLocked = false;
                      } else if (needsUpgrade) {
                        isLocked = true;
                      } else {
                        isLocked = lockStatusMap[chapter._id] ?? true;
                      }

                      const canStart = !isCompleted && !isLocked;
                      const canReview = isCompleted && !isLocked;
                      const canGetFeedback = isCompleted && !isLocked;

                      return (
                        <div
                          key={chapter._id}
                          className={`bg-white border border-gray-200 rounded-md shadow-sm flex flex-col justify-between p-5 transition-opacity ${isLocked && !needsUpgrade ? 'opacity-100' : 'opacity-100'}`}
                        >
                          <div>
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-heading text-lg text-gray-800">{`${idx + 1}. ${formatDisplayTitle(chapter.title)}`}</h3>
                              <div className="flex-shrink-0 ml-2 font-body">
                                {isLocked && !needsUpgrade && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-md  bg-yellow-100 text-yellow-800" title="Locked">🔒 Locked</span>
                                )}
                                {needsUpgrade && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-md  bg-teal-100 text-teal-800" title="Upgrade Required">✨ Pro</span>
                                )}
                                {isCompleted && !isLocked && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-md  bg-green-100 text-green-800" title="Completed">✓ Completed</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="font-body flex items-center justify-end space-x-3 mt-4">
                            {needsUpgrade ? (
                              <Link to="/pricing" className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent shadow-sm text-md  rounded-md text-white bg-[#51c0a6] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#51c0a6] transition">Upgrade to Pro</Link>
                            ) : (
                              <>
                                {canGetFeedback && (
                                  <Link to={`/feedback/${chapter._id}`} className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 shadow-sm text-md  rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition">Feedback</Link>
                                )}
                                {/* wrapper keeps tooltip positioning local */}
                                <div className="relative inline-block">
                                  {/* ▶️ the button */}
                                  <Link
                                    to={(canStart || canReview) ? `/chapter/${chapter._id}` : '#'}
                                    onClick={(e) => {
                                      if (!(canStart || canReview)) e.preventDefault();
                                    }}
                                    aria-disabled={isLocked}
                                    // “peer” emits hover/ focus state the tooltip listens for
                                    className={`
                                      peer
                                      inline-flex items-center justify-center
                                      px-3 py-1.5 text-md  rounded-md shadow-sm
                                      transition
                                      ${isLocked
                                        ? 'bg-gray-400 cursor-not-allowed border border-transparent text-white'
                                        : 'bg-black text-white border border-transparent hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}
                                    `}
                                  >
                                    {isCompleted ? 'Review' : 'Start'}
                                  </Link>

                                  {/* ▶️ the tooltip */}
                                  {isLocked && (
                                    <span
                                      className="
                                        absolute left-1/2 top-full mt-1   /* ⬅️ under the button */
                                        -translate-x-1/2
                                        whitespace-nowrap rounded bg-gray-800 px-2 py-1
                                        text-md text-white
                                        opacity-0 peer-hover:opacity-100 peer-focus:opacity-100
                                        transition-opacity duration-150
                                        z-[80]
                                      "
                                    >
                                      Please complete the previous module before moving on.
                                    </span>
                                  )}
                                </div>

                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </main>
        </div>

        {/* Call to Action (Show only for students) */}
        {user.role === 'student' && (
          <div className="mt-16 p-8 rounded-lg border border-gray-200 text-center bg-white shadow-sm">
            <h2 className="text-xl font-heading mb-3 text-gray-800">Unlock Full Access</h2>
            <p className="font-body mb-6 text-gray-600">
              Ready to dive deeper? Upgrade to Pro to access all modules, our AI copilot, and personalized coaching.
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-black text-white px-6 py-2.5 rounded-md font-body shadow-sm"
            >
              View Pricing Plans
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
